import React, { useState, useEffect, Key } from 'react';
import { Modal } from '@flightstats/airframe-react';
import { modalContentStyle } from "../components/modal/Modal";
import _ from 'lodash';
import Tree from 'rc-tree';
import { DataNode } from 'rc-tree/lib/interface';
import "rc-tree/assets/index.css";
import { getProperty } from "../fsConfig";

export interface TripGroup {
  key: string;
  title: string;
  parent: string;
  children: TripGroup[];
  displayName: string;
  activeAndEntitled: boolean;
  entitled: boolean;
  leafGroup: boolean;
  state: {
    disabled: boolean;
    opened: boolean;
    selected: boolean;
  };
}

interface TripGroupSelectorModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  selectedTripGroupIds: string[];
  includeArchiveGroups: boolean;
  onSubmit: (data: { tripGroups: TripGroup[], includeArchiveGroups: boolean }) => void;
  responseData: any[];
}

const TripGroupSelectorModal: React.FC<TripGroupSelectorModalProps> = ({
  isOpen,
  onRequestClose,
  selectedTripGroupIds,
  includeArchiveGroups,
  onSubmit,
  responseData,
}) => {
  const [allTripGroups, setAllTripGroups] = useState<TripGroup[]>([]);
  const [availableGroups, setAvailableGroups] = useState<TripGroup[]>([]);
  const [treeData, setTreeData] = useState<DataNode[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedTripGroupIds);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [includeArchived, setIncludeArchived] = useState(includeArchiveGroups);
  const { accountName } = getProperty('session', {});

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const fetchTripGroups = async (includeArchived: boolean, responseData: any[]) => {
    try {
      const data = responseData.map(group => {
        let text;
        if (group.attribute === 'ACCOUNT_ID') {
          text = accountName;
        } else {
          text = _.isEmpty(group.name) ? group.attributeValue : group.name;
        }
        return {
          key: group.id,
          title: _.isEmpty(text) ? '(unnamed)' : text,
          parent: group.parentId || '#',
          children: [],
          displayName: group.displayName,
          activeAndEntitled: group.active && group.entitled,
          entitled: group.entitled,
          leafGroup: group.leaf,
          state: {
            disabled: false,
            opened: true,
            selected: selectedTripGroupIds.includes(group.id),
          },
        };
      });
      updateTripGroupsList(includeArchived, data);
    } catch (error) {
      console.error('Error fetching trip groups:', error);
    }
  };

  useEffect(() => {
    fetchTripGroups(includeArchived, responseData);
  }, [selectedTripGroupIds, includeArchiveGroups, responseData]);

  const pruneEmptyParents = (groups: TripGroup[], allGroups: TripGroup[]) => {
    const groupsById: { [key: string]: TripGroup } = {};
    _.forEach(allGroups, group => {
      groupsById[group.key] = group;
    });

    const groupsToKeep: { [key: string]: boolean } = {};
    _.forEach(groups, group => {
      if (group.leafGroup) {
        groupsToKeep[group.key] = true;
        let parentId = group.parent;
        while (parentId && parentId !== '#') {
          groupsToKeep[parentId] = true;
          parentId = groupsById[parentId].parent;
        }
      }
    });

    return _.map(_.keys(groupsToKeep), id => {
      return groupsById[id];
    });
  };

  const updateTripGroupsList = (includeArchiveGroups: boolean, allGroups: TripGroup[]) => {
    const filterProperty = includeArchiveGroups ? 'entitled' : 'activeAndEntitled';
    const potentialGroupsToShow = _.filter(allGroups, filterProperty);
    const availableGroups = pruneEmptyParents(potentialGroupsToShow, allGroups);
    availableGroups.sort((a, b) => a.title.localeCompare(b.title));
    const tree: DataNode[] = [];
    const nodes: { [key: string]: DataNode } = {};
    availableGroups.forEach(item => {
      nodes[item.key] = { ...item, children: [] };
    });
    availableGroups.forEach(item => {
      if (item.parent && item.parent !== '#') {
        nodes[item.parent]?.children?.push(nodes[item.key]);
      } else {
        tree.push(nodes[item.key]);
      }
    });
    setAllTripGroups(allGroups);
    setAvailableGroups(availableGroups);
    setTreeData(tree);
    setExpandedKeys(Object.keys(nodes));

    const selectedGroups = _.filter(
      availableGroups,
      group => group.leafGroup && selectedIds.includes(group.key),
    );
    setSelectedIds(_.map(selectedGroups, 'key'));
    
    _.forEach(allGroups, group => {
      group.state.selected = (_.map(selectedGroups, 'key')).includes(group.key);
    });
  };

  const handleTripGroupSelected = (checked: Key[] | { checked: Key[]; halfChecked: Key[] }) => {
    if (Array.isArray(checked)) {
      setSelectedIds(checked.map(String));
    } else {
      setSelectedIds(checked.checked.map(String));
    }
  };

  const handleIncludeArchivedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeArchived(e.target.checked);
    updateTripGroupsList(e.target.checked, allTripGroups);
  };

  const handleSubmit = () => {
    const selectedGroupsIds = Array.from(new Set(_.flatMap(
      allTripGroups,
      group => {
        if (group.parent !== '#' && group.leafGroup && selectedIds.includes(group.key)) {
          return getAllIds(group, [group.key]);
        }
        return [];
      }
    )));

    if (availableGroups.length === selectedGroupsIds.length + 1) {
      selectedGroupsIds.push(allTripGroups.find(group => group.parent === '#')!.key);
    }

    const selectedGroups = _.filter(allTripGroups, group =>
      selectedGroupsIds.includes(group.key),
    ).sort((a, b) => a.title.localeCompare(b.title));
    onSubmit({
      tripGroups: selectedGroups,
      includeArchiveGroups: includeArchived,
    });
    onRequestClose();
  };

  const getAllIds = (tripGroup: TripGroup, ids: string[] = []) => {
    const idsSet = new Set(ids);
    while (tripGroup && tripGroup.parent && tripGroup.parent !== '#') {
      const parentTripGroup = allTripGroups.find(group => group.key === tripGroup.parent);
      if (parentTripGroup && parentTripGroup.parent !== '#') {
        idsSet.add(tripGroup.parent);
      }
      tripGroup = parentTripGroup!;
    }
    return Array.from(idsSet);
  };

  const handleCancel = () => {
    onRequestClose();
  };

  const handleClearSelections = () => {
    setSelectedIds([]);
  };

  const isSubmitDisabled = () => {
    return _.isEmpty(selectedIds);
  };

  return (
    <Modal
      isOpen={isOpen}
      contentStyle={modalContentStyle}
      onRequestClose={handleCancel}
    >
      <div className="issue-resolution-modal" style={{ width: '50%', margin: 'auto' }}>
        <form className="form" onSubmit={handleSubmit}>
          <div className="title">
            SELECT TRIP GROUPS
            <a onClick={handleCancel}>
              <i
                id="modalCancelled"
                className="cancel pull-right glyphicon icon-white glyphicon-remove"
              ></i>
            </a>
          </div>

          <div className="content">
            <div className="choices">
              <div className="row field-sep tree-selector">
                <Tree
                  checkable
                  onCheck={handleTripGroupSelected}
                  checkedKeys={selectedIds}
                  expandedKeys={expandedKeys}
                  onExpand={(expandedKeys) => setExpandedKeys(expandedKeys as string[])}
                  treeData={treeData}
                  selectable={false}
                />
              </div>

              <div className="row field-sep">
                <label>
                  <input
                    type="checkbox"
                    checked={includeArchived}
                    onChange={handleIncludeArchivedChange}
                  />
                  Include Archived Groups
                </label>
              </div>

              <div className="action">
                <button
                  type="button"
                  className="btn btn-default button-clear"
                  onClick={handleClearSelections}
                  disabled={isSubmitDisabled()}
                  style={{ marginRight: '5px' }}
                >
                  CLEAR ALL
                </button>
                <button
                  type="submit"
                  className="btn btn-default button-action"
                  disabled={isSubmitDisabled()}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default TripGroupSelectorModal;